import { Component, OnInit, OnDestroy } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Platform } from '@ionic/angular';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  Subject,
  takeUntil,
} from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthService } from 'src/app/firebase/auth.service';
import { showLoading } from 'src/app/shared/components/modals/loading';
import { ServicesStore } from './services.store';
import { FormService } from 'src/app/shared/components/form/form.service';
import { ServicesService } from './services.service';
import {
  IServicesFilter,
  serviceDiscordModalData,
  servicePremiumModalData,
} from './services';
import { PaymentsStore } from 'src/app/payments/payments-store';
import { DateService } from 'src/app/services/date.service';
import { UserService } from 'src/app/auth/user.service';
import { CrudHelper } from 'src/app/firebase/helpers/crudHelper';
import { includesIgnoreCase } from 'src/utils';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-services',
  templateUrl: './services.component.html',
  styleUrls: ['./services.component.scss'],
})
export class ServicesComponent implements OnInit, OnDestroy {
  searchString: BehaviorSubject<string> = new BehaviorSubject<string>('');

  status;
  loader = showLoading();

  myContent: boolean = false;
  userId: string;
  creator;
  items;
  filterOptions;
  startDate;
  endDate;
  isOpenFormModal = false;
  filter;
  hasMembership = false;

  currentTime: Date = new Date();

  isPremium: boolean;
  isOpenDiscordModal = false;
  premiumModalData = servicePremiumModalData;
  serviceDiscordModalData = serviceDiscordModalData;
  isUserPremiumModalOpen = false;
  currentUser;
  infoSnapshot;
  userInfo;
  color = 'dark';
  today = new Date().getTime() / 1000;

  isMobile = this.platform.platforms().includes('capacitor');
  isIosApp = this.platform.platforms().includes('ios') && this.isMobile;

  isAdmin = false;

  private destroy$ = new Subject<void>();

  constructor(
    private servicesStore: ServicesStore,
    private servicesService: ServicesService,
    private dateService: DateService,
    private fs: FormService,
    private fireStore: AngularFirestore,
    private authService: AuthService,
    private paymentsStore: PaymentsStore,
    private platform: Platform,
    private userService: UserService,
    private crudHelper: CrudHelper
  ) {}

  async ngOnInit() {
    this.getUser();
    this.listenInfoDetails();
    this.listenItemsChanges();
    this.listenStatusChanges();
    this.listenFilter();

    this.servicesStore.selectShowFormModal().subscribe(isOpen => {
      this.isOpenFormModal = isOpen;
    });

    this.servicesStore.getItems(false);
  }

  ionViewWillEnter() {
    if (this.userId !== this.authService.getCurrentUser().uid) {
      this.ngOnInit();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.infoSnapshot) {
      this.infoSnapshot.unsubscribe();
    }
    this.fs.formInitialValues.next({ myContent: null });
    this.fs.servicesMenuData$.next(false);
  }

  async openModal() {
    const userId = this.currentUser?.uid || this.authService.getCurrentUser()?.uid;
    this.hasMembership =
      this.hasMembership || (await this.authService.getMembership(userId));
    this.isPremium = !!this.userService.isPremium;
    this.isAdmin = !!this.userService.isAdmin;

    if (!environment.production) {
      console.info('[premium-gate]', {
        gate: 'services-search',
        uid: userId,
        isPremium: this.isPremium,
        isPaidPremium: !!this.userService.isPaidPremium,
        hasMembership: this.hasMembership,
        isAdmin: this.isAdmin,
        decision:
          this.hasMembership || this.isPremium || this.isAdmin
            ? 'open'
            : 'chat',
      });
    }

    if (this.hasMembership || this.isPremium || this.isAdmin) {
      this.servicesStore.updateShowFormModal(true);
    } else {
      this.isOpenDiscordModal = true;
    }
  }

  closeModal(e) {
    e ? this.servicesStore.updateShowFormModal(false) : null;
  }

  onSearchChange(searchString) {
    this.searchString.next(searchString);
  }

  private listenFilter() {
    this.fs.servicesMenuData$.subscribe((data: IServicesFilter) => {
      if (data) {
        this.startDate = new Date(data.startDate).getTime() / 1000;
      }

      data.endDate
        ? (this.endDate = new Date(data.endDate).getTime() / 1000)
        : null;

      const initialData = this.servicesService.checkFilter(data);
      if (initialData) {
        this.color = 'success';
      } else {
        this.color = 'dark';
      }
      this.filter = data;
      this.myContent = (data as IServicesFilter).myContent;
      this.servicesStore.getItems(data);
    });
  }

  private listenStatusChanges() {
    this.servicesStore
      .selectLoading()
      .pipe(takeUntil(this.destroy$))
      .subscribe(async loading => {
        loading ? (await this.loader).present() : (await this.loader).dismiss();
      });
  }

  private listenInfoDetails() {
    const { uid } = this.authService.isUserAuth();
    this.infoSnapshot = this.fireStore
      .collection('info')
      .doc(uid)
      .get()
      .subscribe(data => {
        this.userInfo = data.data() as any;
      });
  }

  private listenItemsChanges() {
    combineLatest([
      this.servicesStore.selectItems(),
      this.searchString,
      this.fs.selectedEndDate,
      this.fs.selectedStartDate,
    ])
      .pipe(
        takeUntil(this.destroy$),
        map(([items, searchString]) => {
          if (!items) {
            return [];
          }

          let filteredItems = items;

          // Only apply search filter if there's a search string
          if (searchString) {
            filteredItems = filteredItems.filter(item =>
              includesIgnoreCase(item.title, searchString)
            );
          }

          return filteredItems;
        }),
        switchMap((items: any[]) => {
          if (!items?.length) return (this.items = []);
          const users = this.crudHelper.getCollectionItems({
            collectionName: 'users',
            queryFn: null,
          });
          return users.pipe(
            filter(users => !!users),
            map(users => {
              const itemsWithUserData = items.map(item => {
                const user: any = users.find(
                  (user: any) => user.id === item.creator.id
                );
                if (user) return Object.assign({ ...item, creator: user });
                return item;
              });
              return itemsWithUserData;
            })
          );
        })
      )
      .subscribe(items => {
        const scheduledItems = this.dateService.getScheduledItems(items, this.myContent);
        this.items = scheduledItems.filter(
          item => !this.currentUser?.blockList?.includes(item.creator.id)
        );
      });
  }

  private getUser() {
    this.userService.currentUser$
      .pipe(
        takeUntil(this.destroy$),
        filter(user => !!user.uid)
      )
      .subscribe(async user => {
        this.currentUser = user;
        this.isPremium = this.userService.isPremium;
        this.isAdmin = this.userService.isAdmin;
        this.userId = user.uid;
        this.userInfo = this.userService.balanceInfo;
        this.hasMembership = await this.authService.getMembership(
          this.currentUser.uid
        );
      });
  }
}

